import {useMessage} from '@coolcolduk/ui-mui';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {useState} from 'react';
import {Link, useNavigate} from 'react-router';
import useCreateGenerationJobProject from '../../../hook/generationJob/useCreateGenerationJobProject';
import type {GenerationJobProjectInput, OutputQuality, OutputShape} from '../../../hook/generationJob/schema';
import useGdriveTokens from '../../../hook/useGdriveTokens';

const OUTPUT_QUALITY_OPTIONS: OutputQuality[] = ['auto', 'low', 'medium', 'high'];
const OUTPUT_SHAPE_OPTIONS: OutputShape[] = ['auto', 'square', 'portrait', 'landscape'];

export default function ImageToImageNewProjectPage() {
  const {showMessage} = useMessage();
  const navigate = useNavigate();
  const {tokens: gdriveTokens, loading: gdriveTokensLoading} = useGdriveTokens();

  const [projectName, setProjectName] = useState('');
  const [gdriveTokenId, setGdriveTokenId] = useState('');
  const [sourceImageFileId, setSourceImageFileId] = useState('');
  const [sourceImageUrl, setSourceImageUrl] = useState('');
  const [sheetUrlOrId, setSheetUrlOrId] = useState('');
  const [outputFolderUrlOrId, setOutputFolderUrlOrId] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [edit, setEdit] = useState(false);
  const [outputQuality, setOutputQuality] = useState<OutputQuality>('auto');
  const [outputShape, setOutputShape] = useState<OutputShape>('auto');

  const [createProject, {loading: createProjectLoading}] = useCreateGenerationJobProject({
    onCompleted: () => {
      showMessage('Project created');
      navigate('/image-to-image');
    },
    onError: (e) => showMessage(`Error: ${e.message}`, undefined, 'error'),
  });

  const handleSubmit = () => {
    if (!gdriveTokenId.trim()) {
      showMessage('Select a Google Drive account', undefined, 'warning');
      return;
    }
    if (!sheetUrlOrId.trim() || !outputFolderUrlOrId.trim()) {
      showMessage('Fill sheet and output folder', undefined, 'warning');
      return;
    }
    createProject({
      name: projectName || undefined,
      gdriveTokenId: gdriveTokenId.trim(),
      sourceImageFileId: sourceImageFileId.trim() || undefined,
      sourceImageUrl: sourceImageUrl.trim(),
      sheetUrlOrId: sheetUrlOrId.trim(),
      outputFolderUrlOrId: outputFolderUrlOrId.trim(),
      systemPrompt: systemPrompt.trim() || undefined,
      edit,
      outputQuality,
      outputShape,
    } as GenerationJobProjectInput);
  };

  if (gdriveTokensLoading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Add project</Typography>
      <Typography variant="body2" color="text.secondary">
        Create a new image-to-image project. Choose a linked Google Drive account and provide the source image, sheet,
        and output folder (paste links or use picker when available).
      </Typography>

      <Button component={Link} to="/image-to-image" variant="outlined" sx={{alignSelf: 'flex-start'}}>
        Back to projects
      </Button>

      {gdriveTokens.length === 0 ? (
        <Typography color="text.secondary">
          No Google Drive account linked. Go to{' '}
          <Link to="/settings/gdrive-accounts">Settings → Google Drive accounts</Link> to add one, then come back to
          create a project.
        </Typography>
      ) : (
        <>
          <FormControl fullWidth required>
            <InputLabel id="gdrive-account-label">Google Drive account</InputLabel>
            <Select
              labelId="gdrive-account-label"
              value={gdriveTokenId}
              label="Google Drive account"
              onChange={(e) => setGdriveTokenId(e.target.value)}
            >
              {gdriveTokens.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name} {t.email ? `(${t.email})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Project name (optional)"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
          />

          <TextField
            fullWidth
            label="Source image (optional) – paste Drive link or file id"
            value={sourceImageUrl || sourceImageFileId}
            onChange={(e) => {
              const v = e.target.value.trim();
              if (v.startsWith('http')) {
                setSourceImageUrl(v);
                setSourceImageFileId('');
              } else {
                setSourceImageFileId(v);
                setSourceImageUrl('');
              }
            }}
            placeholder="https://drive.google.com/... or file id"
          />

          <FormControlLabel
            control={<Checkbox checked={edit} onChange={(e) => setEdit(e.target.checked)} />}
            label="Use edit endpoint (required for reference/source images; when unchecked, generations endpoint is used — text-to-image only, no input images)"
          />

          <FormControl fullWidth>
            <InputLabel id="output-quality-label">Output quality</InputLabel>
            <Select
              labelId="output-quality-label"
              value={outputQuality}
              label="Output quality"
              onChange={(e) => setOutputQuality(e.target.value as OutputQuality)}
            >
              {OUTPUT_QUALITY_OPTIONS.map((q) => (
                <MenuItem key={q} value={q}>
                  {q}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="output-shape-label">Output shape</InputLabel>
            <Select
              labelId="output-shape-label"
              value={outputShape}
              label="Output shape"
              onChange={(e) => setOutputShape(e.target.value as OutputShape)}
            >
              {OUTPUT_SHAPE_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="System prompt (optional)"
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            placeholder="Optional instructions applied to every image in this project…"
          />

          <TextField
            fullWidth
            label="Google Sheet URL (columns: filename, prompt, status)"
            value={sheetUrlOrId}
            onChange={(e) => setSheetUrlOrId(e.target.value)}
            placeholder="https://docs.google.com/spreadsheets/d/..."
          />

          <TextField
            fullWidth
            label="Output folder (Google Drive link or folder id)"
            value={outputFolderUrlOrId}
            onChange={(e) => setOutputFolderUrlOrId(e.target.value)}
            placeholder="https://drive.google.com/..."
          />

          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={createProjectLoading}>
            Add project
          </Button>
        </>
      )}
    </Stack>
  );
}
